import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import tr from './locales/tr';

export type Language = 'tr' | 'en';

type Dictionary = typeof tr;

type I18nContextValue = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const STORAGE_KEY = 'sys-language';

export const LANGUAGE_OPTIONS: Array<{ code: Language; label: string; flag: string }> = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

const dictionaries: Record<Language, Dictionary> = {
  tr,
  en: tr,
};

const I18nContext = createContext<I18nContextValue | null>(null);

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'tr';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'tr' || stored === 'en') return stored;
  const browserLang = window.navigator.language.toLowerCase();
  if (browserLang.startsWith('en')) return 'en';
  return 'tr';
}

function getByPath(source: Record<string, unknown>, key: string): unknown {
  return key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, source);
}

function applyParams(text: string, params?: Record<string, string | number>) {
  if (!params) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => String(params[key] ?? ''));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nContextValue>(() => ({
    lang,
    setLang,
    t: (key, params) => {
      const dictionary = dictionaries[lang] ?? tr;
      const value = getByPath(dictionary as Record<string, unknown>, key);
      if (typeof value === 'string') return applyParams(value, params);
      const fallback = getByPath(tr as Record<string, unknown>, key);
      if (typeof fallback === 'string') return applyParams(fallback, params);
      return key;
    },
  }), [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
}
